import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  // Pending branch-select state: populated after /auth/login when the user must still pick
  // a branch and/or enter a Branch PIN. ProtectedRoute reads this and renders <SelectBranch />.
  const [pendingBranches, setPendingBranches] = useState(null);
  const [needsBranchPin, setNeedsBranchPin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("careo_token");
    if (!token) {
      setUser(null);
      return;
    }
    const pending = JSON.parse(localStorage.getItem("careo_pending_branches") || "null");
    if (pending) setPendingBranches(pending);
    setNeedsBranchPin(localStorage.getItem("careo_needs_branch_pin") === "1");
    api.get("/auth/me")
      .then(({ data }) => {
        setUser(data.user);
        setHospital(data.hospital || null);
        setBranch(data.branch || null);
      })
      .catch(() => setUser(null));
  }, []);

  const _persistPending = (branches, needsPin) => {
    if (branches && branches.length) localStorage.setItem("careo_pending_branches", JSON.stringify(branches));
    else localStorage.removeItem("careo_pending_branches");
    if (needsPin) localStorage.setItem("careo_needs_branch_pin", "1");
    else localStorage.removeItem("careo_needs_branch_pin");
    setPendingBranches(branches && branches.length ? branches : null);
    setNeedsBranchPin(!!needsPin);
  };

  const login = async (email, password, hospitalCode) => {
    const body = { email, password };
    if (hospitalCode) body.hospital_code = hospitalCode;
    const { data } = await api.post("/auth/login", body);
    if (hospitalCode) localStorage.setItem("careo_last_hospital_code", hospitalCode);
    // 2FA short-circuit — caller (Login.jsx) must collect the second factor and call /auth/2fa/login-verify.
    if (data.requires_2fa) {
      return { requires_2fa: true, pre_auth_token: data.pre_auth_token, user: data.user };
    }
    localStorage.setItem("careo_token", data.access_token);
    // Three cases:
    //  (1) needs_branch_select=true                → show branch picker (then PIN prompt if needs_pin)
    //  (2) needs_branch_select=false + needs_pin   → single branch was auto-fetched server side, but PIN still needed
    //  (3) neither                                  → fully logged in
    if (data.needs_branch_select || data.needs_branch_pin) {
      _persistPending(data.available_branches, data.needs_branch_pin);
      setUser(data.user);
      return {
        needs_branch_select: data.needs_branch_select,
        needs_branch_pin: data.needs_branch_pin,
        available_branches: data.available_branches,
      };
    }
    _persistPending(null, false);
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setHospital(me.data.hospital || null);
    setBranch(me.data.branch || null);
    return { needs_branch_select: false, needs_branch_pin: false, user: me.data.user };
  };

  const verify2fa = async (preAuthToken, token) => {
    const { data } = await api.post("/auth/2fa/login-verify", { pre_auth_token: preAuthToken, token });
    localStorage.setItem("careo_token", data.access_token);
    if (data.needs_branch_select || data.needs_branch_pin) {
      _persistPending(data.available_branches, data.needs_branch_pin);
      setUser(data.user);
      return { needs_branch_select: data.needs_branch_select, needs_branch_pin: data.needs_branch_pin, available_branches: data.available_branches };
    }
    _persistPending(null, false);
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setHospital(me.data.hospital || null);
    setBranch(me.data.branch || null);
    return { needs_branch_select: false, needs_branch_pin: false, user: me.data.user };
  };

  const selectBranch = async (branchId, branchPin) => {
    const body = { branch_id: branchId };
    if (branchPin) body.branch_pin = branchPin;
    const { data } = await api.post("/auth/select-branch", body);
    localStorage.setItem("careo_token", data.access_token);
    _persistPending(null, false);
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setHospital(me.data.hospital || null);
    setBranch(me.data.branch || null);
    return me.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {/* ignore */}
    localStorage.removeItem("careo_token");
    _persistPending(null, false);
    setUser(null);
    setHospital(null);
    setBranch(null);
  };

  return (
    <AuthContext.Provider value={{ user, hospital, branch, pendingBranches, needsBranchPin, login, verify2fa, selectBranch, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
